import React, { useState } from 'react';
import { ReportHeader } from '../../header/report-header';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import 'primereact/resources/themes/lara-light-purple/theme.css';
import { LecturesSideBarFilter } from './side-bar-filter/lectures-side-bar-filter';
import Colors from '../../../../utils/colors';
import { LecturesSideBarFilterDatamodel } from './side-bar-filter/lectures-side-bar-filter-datamodel';
import { getLecturesReportData } from './operations/get-lectures-report-data';
import { ReportLecturesType } from '../../../../api/types/reports/education/report-lectures.type';
import { format } from 'date-fns';
import dateUtils from '../../../../utils/date-format';
import dateFormat from '../../../../utils/date-format';

export const ReportLectures = () => {
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<LecturesSideBarFilterDatamodel>();
  const [data, setData] = useState<ReportLecturesType[]>();
  // Mock data for demonstration
  const lecturesData = [
    {
      branch: 'ירושלים',
      program: 'הנדסת תוכנה',
      programId: '2023',
      courseName: 'מבוא לתכנות',
      lectureDate: '2024-03-15',
      scheduledStartTime: '09:00',
      scheduledEndTime: '12:00',
      actualStartTime: '09:05',
      actualEndTime: '12:10',
      lecturer: 'ד"ר ישראל ישראלי',
      status: 'מפגש שלא נסגר',
    },
    {
      branch: 'תל אביב',
      program: 'מדעי המחשב',
      programId: '2023',
      courseName: 'אלגוריתמים',
      lectureDate: '2024-03-14',
      scheduledStartTime: '13:00',
      scheduledEndTime: '17:00',
      actualStartTime: '13:15',
      actualEndTime: '17:30',
      lecturer: 'פרופ׳ יעקב כהן',
      status: 'מפגש חורג בשעות',
    },
  ];

  const getTimeExceptionStyle = (minutes: number) => ({
    color: minutes > 0 ? Colors.dangerText : Colors.approveText2,
  });

  const calculateTimeDifference = (
    scheduled: string,
    actual: string,
  ): number => {
    const [scheduledHours, scheduledMinutes] = scheduled.split(':').map(Number);
    const [actualHours, actualMinutes] = actual.split(':').map(Number);
    return (
      actualHours * 60 +
      actualMinutes -
      (scheduledHours * 60 + scheduledMinutes)
    );
  };

  const getStatusSeverity = (status: string) => {
    const severityMap: { [key: string]: 'success' | 'warning' | 'danger' } = {
      'מפגש שלא נסגר': 'danger',
      'מפגש חורג בשעות': 'warning',
    };
    return severityMap[status] || 'info';
  };

  const getStatusStyle = (status: string) => {
    const isError = status === 'מפגש שלא נסגר';
    return {
      backgroundColor: isError ? '#FEE2E2' : '#FEF3C7',
      color: isError ? Colors.dangerText : Colors.warningYelloText,
      padding: '4px 8px',
      borderRadius: '4px',
      width: '120px',
      textAlign: 'center' as const,
      display: 'inline-block',
    };
  };

  const statusTemplate = (rowData: any) => (
    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
      <div style={getStatusStyle(rowData.status)}>{rowData.status}</div>
    </div>
  );

  const formatDateTime = (date: Date) => {
    return format(date, 'HH:mm');
  };

  const startTimeTemplate = (rowData: ReportLecturesType) => {
    // Get the earliest start time from lecturers
    const actualStartTime =
      rowData.lecturers.length > 0
        ? rowData.lecturers.reduce((earliest, lecturer) =>
            earliest.workingStartTime < lecturer.workingStartTime
              ? earliest
              : lecturer,
          ).workingStartTime
        : null;

    const difference = actualStartTime
      ? calculateTimeDifference(
          formatDateTime(rowData.startTime),
          formatDateTime(actualStartTime),
        )
      : 0;

    return (
      <div style={{ textAlign: 'right' }}>
        {formatDateTime(rowData.startTime)}
        {difference !== 0 && actualStartTime && (
          <span style={getTimeExceptionStyle(difference)}>
            {' '}
            (+{Math.abs(difference)} דק׳)
          </span>
        )}
      </div>
    );
  };

  const endTimeTemplate = (rowData: ReportLecturesType) => {
    // Get the latest end time from lecturers
    const actualEndTime =
      rowData.lecturers.length > 0
        ? rowData.lecturers.reduce((latest, lecturer) =>
            latest.workingEndTime > lecturer.workingEndTime ? latest : lecturer,
          ).workingEndTime
        : null;

    const difference = actualEndTime
      ? calculateTimeDifference(
          formatDateTime(rowData.endTime),
          formatDateTime(actualEndTime),
        )
      : 0;

    return (
      <div style={{ textAlign: 'right' }}>
        {formatDateTime(rowData.endTime)}
        {difference !== 0 && actualEndTime && (
          <span style={getTimeExceptionStyle(difference)}>
            {' '}
            (+{Math.abs(difference)} דק׳)
          </span>
        )}
      </div>
    );
  };

  const handleSearch = async (filters: LecturesSideBarFilterDatamodel) => {
    setFilters(filters);
    await getLecturesReportData(filters, setLoading, setData);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <ReportHeader
        title="דו״ח מפגשים חורגים בהצלחה"
        description="דו״ח זה מציג מידע על מפגשים שלא נסגרו בהצלחה, כולל מפגשים שלא נסגרו כלל ומפגשים החורגים בשעות."
        metadata={[
          { label: 'מטרת הדו״ח', value: 'מעקב אחר סגירת מפגשים' },
          { label: 'תדירות עדכון', value: 'יומי' },
        ]}
        category="education"
      />

      <div style={{ display: 'flex', gap: '20px' }}>
        <div
          style={{
            height: 'calc(100vh - 230px)',
            minHeight: '100%',
            width: '300px',
            minWidth: '250px',
          }}
        >
          <LecturesSideBarFilter onSearch={handleSearch} />
        </div>

        {/* Table Section */}
        <div style={{ flex: 1 }}>
          <DataTable
            value={data}
            rows={10}
            rowHover
            rowClassName={() => 'cursor-pointer'}
            rowsPerPageOptions={[5, 10, 25, 50]}
            emptyMessage={
              <div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>
            }
            dataKey="id"
            scrollable
            scrollHeight="calc(100vh - 300px)"
            tableStyle={{
              direction: 'rtl',
            }}
          >
            <Column
              field="branchId"
              header="שלוחה"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="abstractProgramName"
              header="תוכנית לימודים"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="annualSerialProgramNumber"
              header="מזהה תוכנית"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="courseName"
              header="שם קורס"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="lectureDate"
              header="תאריך מפגש"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData: ReportLecturesType) =>
                dateFormat.displayFormatDate(rowData.startTime)
              }
            />
            <Column
              field="scheduledStartTime"
              header="שעת התחלה"
              body={startTimeTemplate}
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="scheduledEndTime"
              header="שעת סיום"
              body={endTimeTemplate}
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="lecturer"
              header="מרצה"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData: ReportLecturesType) => (
                <div style={{ textAlign: 'right' }}>
                  {rowData.lecturers
                    .map(
                      (lecturer) =>
                        `${lecturer.firstName} ${lecturer.lastName}`,
                    )
                    .join(', ')}
                </div>
              )}
            />
            <Column
              field="status"
              header="סטטוס"
              body={statusTemplate}
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
          </DataTable>
        </div>
      </div>
    </div>
  );
};
